import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/js/src/collapse.js";
import "./App.css";
import "@fortawesome/fontawesome-free/css/all.min.css";

import AuthService from "./services/auth.service";

import BoardLogin from "./components/BoardLogin";
import BoardRegister from "./components/BoardRegister";
import BoardResetPwd from "./components/BoardResetPwd";
import BoardHome from "./components/BoardHome";
import BoardProfile from "./components/BoardProfile";
import BoardDevices from "./components/BoardDevices";
import BoardDiag from "./components/BoardDiag";
import BoardPerfs from "./components/BoardPerfs";
import BoardRace from "./components/BoardRace";
import BoardOverlay from "./components/BoardOverlay";
import BoardTracks from "./components/BoardTracks";
import BoardTagsTracking from "./components/BoardTagsTracking";
import BoardTheme from "./components/BoardTheme";

import EventBus from "./common/EventBus";
import MQTTService from './services/MQTTService';

const App = () => {

  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(undefined);

  const [isNavCollapsed, setIsNavCollapsed] = useState(true);

  const [isDark, setIsDark] = useState(localStorage.getItem('themeDark') === 'true');

  const location = useLocation();

  const isAdmin = currentUser?.roles?.includes("admin_smart")
  const isPartner = currentUser?.roles?.includes("partner")

  const isDesktop = !window.mobileCheck();

  // Suppress specific ResizeObserver error messages
  const resizeObserverLoopErrRe = /ResizeObserver loop completed with undelivered notifications\./;
  const disconnectErrRe = /Attempting to use a disconnected port object\./;

  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (args[0] && resizeObserverLoopErrRe.test(args[0])) {
      console.log('ResizeObserver loop completed with undelivered notifications.');
      // Ignore the specific ResizeObserver error
      return;
    }
    if (args[0] && disconnectErrRe.test(args[0])) {
      console.log('Attempting to use a disconnected port object.');
      // Ignore the specific ResizeObserver error
      return;
    }
    // Log all other errors normally
    originalConsoleError.apply(console, args);
  };


  const NavLink = ({ to, children, ...props }) => {
    return (
      <Link to={to} onClick={handleNavCollapse} {...props}>
        {children}
      </Link>
    );
  };

  const handleNavCollapse = () => setIsNavCollapsed((prevCollapse) => !prevCollapse);

  const logOut = () => {
    console.log("logout");
    // Save the current path to localStorage
    if ((location.pathname !== "/") && (location.pathname !== "/app/login"))
      localStorage.setItem('lastPath', location.pathname);
    AuthService.logout();
    MQTTService.disconnect();

    MQTTService.setUserAndPassword(null, null, null);

    setCurrentUser(undefined);
    navigate('/app/login');
  };

  useEffect(() => {
    const user = AuthService.getCurrentUser();

    if (user) {
      setCurrentUser(user);

      // Check for the last visited path
      const lastPath = localStorage.getItem('lastPath');
      if (lastPath && location.pathname === "/app/") {
        navigate(lastPath);
      }
    }

    EventBus.on("logout", () => {
      logOut();
    });

    EventBus.on("login", (user) => {
      setCurrentUser(user);
    });

    const checkTokenExpiration = () => {
      let tokenExpireAt = localStorage.getItem('tokenExpireAt');

      if (Date.now() > tokenExpireAt) {
        EventBus.dispatch("logout");
      }
    };

    const interval = setInterval(checkTokenExpiration, 10000);

    return () => {
      EventBus.remove("logout");
      clearInterval(interval);
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (isDark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    localStorage.setItem('themeDark', isDark);
  }, [isDark]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (location.pathname === "/app/login" || location.pathname === "/app" || location.pathname === "/") {
          window.location.reload();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  

  return (
    <div className="main" style={{ minHeight: "100vh" }}>
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <div className="container-fluid">
          <Link to="/app/" className="navbar-brand font-weight-bolder">
            <img src="/app/web_hi_res_64.png" alt="SmartElec Logo" width="40" height="40" className="mr-2" />
            SmartElec
          </Link>
          <button
            className="custom-toggler navbar-toggler"
            type="button"
            data-toggle="collapse.show"
            data-target="#navbarsExample09"
            aria-controls="navbarsExample09"
            aria-expanded={isNavCollapsed ? false : true}
            aria-label="Toggle navigation"
            onClick={handleNavCollapse}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`${isNavCollapsed ? "collapse" : ""} navbar-collapse justify-content-end`} id="navbarsExample09">
            {currentUser && currentUser.email !== undefined && currentUser.email !== null ? (
              <>
                <NavLink className={`nav-link text-light ${location.pathname === "/app/profile" ? "font-weight-bold" : ""}`} to="/app/profile">Profile</NavLink>
                <NavLink className={`nav-link text-light ${location.pathname === "/app/devices" ? "font-weight-bold" : ""}`} to="/app/devices">Devices</NavLink>
                <NavLink className={`nav-link text-light ${location.pathname === "/app/perfs" ? "font-weight-bold" : ""}`} to="/app/perfs">Performances</NavLink>
                <NavLink className={`nav-link text-light ${location.pathname === "/app/race" ? "font-weight-bold" : ""}`} to="/app/race">Race</NavLink>
                {(isDesktop) && <NavLink className={`nav-link text-light ${location.pathname === "/app/tracks" ? "font-weight-bold" : ""}`} to="/app/tracks">Tracks</NavLink>}
                <NavLink className={`nav-link text-light ${location.pathname === "/app/tags_tracking" ? "font-weight-bold" : ""}`} to="/app/tags_tracking">Tags Tracking</NavLink>
                {((isAdmin || isPartner) && isDesktop) && <NavLink className={`nav-link text-light ${location.pathname === "/app/diag" ? "font-weight-bold" : ""}`} to="/app/diag">Diagnostic</NavLink>}
                {(isDesktop) && <NavLink className={`nav-link text-light ${location.pathname === "/app/theme" ? "font-weight-bold" : ""}`} to="/app/theme">Theme</NavLink>}
                <a
                  className="nav-link btn btn-link text-light"
                  onClick={() => setIsDark(!isDark)}
                  style={{ width: '65px' }}
                >
                  <i className={`fas fa-2x ${isDark ? 'fa-moon' : 'fa-sun'}`} />
                </a>
                <a className="nav-link text-muted disabled">{currentUser.email}</a>
                <button className="nav-link btn btn-link text-light" onClick={logOut}>Logout</button>
              </>
            ) : (
              <>
                <NavLink className="nav-link text-light" to="/app/login">Login</NavLink>
                <NavLink className="nav-link text-light" to="/app/register">Sign Up</NavLink>
              </>
            )}
          </div>
        </div>
      </nav >

      <div className="container mt-3">
        <Routes>
          <Route exact path={"/app/"} element={<BoardLogin />} />
          <Route exact path={"/app/home"} element={<BoardHome />} />
          <Route exact path="/app/login" element={<BoardLogin />} />
          <Route exact path="/app/register" element={<BoardRegister />} />
          <Route exact path="/app/reset_pwd" element={<BoardResetPwd />} />
          <Route exact path="/app/profile" element={<BoardProfile />} />
          <Route path="/app/perfs" element={<BoardPerfs />} />
          <Route path="/app/race" element={<BoardRace />} />
          <Route path="/app/overlay" element={<BoardOverlay />} />
          <Route path="/app/devices" element={<BoardDevices />} />
          <Route path="/app/tags_tracking" element={<BoardTagsTracking />} />
          <Route path="/app/diag" element={<BoardDiag />} />
          <Route path="/app/tracks" element={<BoardTracks />} />
          <Route path="/app/theme" element={<BoardTheme />} />

        </Routes>
      </div>
    </div >
  );
};

export default App;
